import { useEffect, useRef, useState } from "react";
import type { InputHTMLAttributes, ReactNode, RefObject, TextareaHTMLAttributes } from "react";

/** What a menu often wants (S21): food and diet, to point out, and signs a keyboard hides. */
const GROUPS: { label: string; chars: string[] }[] = [
  { label: "Essen", chars: ["🌶️", "🌱", "🥕", "🥦", "🍅", "🧄", "🧅", "🍋", "🐟", "🦐", "🦑", "🥩", "🍗", "🥓", "🧀", "🥚", "🍞", "🥗", "🍲", "🍰", "🍦", "🍷", "🍺", "☕"] },
  { label: "Hervorheben", chars: ["⭐", "★", "♥", "✓", "✨", "🔥", "👍", "😋", "🎉", "👨‍🍳", "🆕", "❗"] },
  { label: "Zeichen", chars: ["€", "½", "¼", "¾", "°", "·", "–", "„", "“", "»", "«", "→", "✕", "×", "®", "©"] },
];

/** The keys that open the system's own choice, for whoever wants more. */
const MORE = "Mehr: am Mac ctrl + ⌘ + Leertaste, unter Windows ⊞ + . (Punkt)";

/**
 * A field with „☺“ beside it: a short choice of emojis and signs, put in where the cursor stands
 * (S21). For the description of a dish, texts, notes – wherever a menu says something.
 */
export function SymbolField({ target, value, onChange, children, disabled }: { target: RefObject<HTMLInputElement | HTMLTextAreaElement | null>; value: string; onChange: (v: string) => void; children: ReactNode; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  // The cursor as it was when the button took the focus.
  const at = useRef<[number, number] | null>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const put = (c: string) => {
    const el = target.current;
    const [a, b] = at.current ?? [value.length, value.length];
    onChange(value.slice(0, a) + c + value.slice(b));
    const caret = a + c.length;
    at.current = [caret, caret];
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  };

  return (
    <span className="symbol-field" ref={box}>
      {children}
      {!disabled && (
        <button
          type="button"
          className="symbol-button"
          onPointerDown={() => {
            const el = target.current;
            at.current = el && document.activeElement === el ? [el.selectionStart ?? value.length, el.selectionEnd ?? value.length] : [value.length, value.length];
          }}
          // In a label the click would also go to its field: only the button's.
          onClick={(e) => [e.preventDefault(), setOpen(!open)]}
          aria-label="Symbol einfügen"
          aria-expanded={open}
          title="Symbol einfügen"
          data-testid="symbol-button"
        >
          ☺
        </button>
      )}
      {open && (
        <span className="symbol-pop" role="dialog" aria-label="Symbol einfügen" data-testid="symbol-pop">
          {GROUPS.map((g) => (
            <span key={g.label} className="symbol-group">
              <span className="muted small">{g.label}</span>
              <span className="symbol-chars">
                {g.chars.map((c) => (
                  <button key={c} type="button" onClick={(e) => [e.preventDefault(), put(c)]} aria-label={`${c} einfügen`}>
                    {c}
                  </button>
                ))}
              </span>
            </span>
          ))}
          <span className="hint">{MORE}</span>
        </span>
      )}
    </span>
  );
}

/** An input with „☺“: `onValue` gets the text, typed or with a symbol put in. */
export function SymbolInput({ value, onValue, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & { value: string; onValue: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <SymbolField target={ref} value={value} onChange={onValue} disabled={Boolean(rest.readOnly || rest.disabled)}>
      <input ref={ref} value={value} onChange={(e) => onValue(e.target.value)} {...rest} />
    </SymbolField>
  );
}

/** A textarea with „☺“. */
export function SymbolTextarea({ value, onValue, ...rest }: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & { value: string; onValue: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  return (
    <SymbolField target={ref} value={value} onChange={onValue} disabled={Boolean(rest.readOnly || rest.disabled)}>
      <textarea ref={ref} value={value} onChange={(e) => onValue(e.target.value)} {...rest} />
    </SymbolField>
  );
}
